import React, { useState, useEffect } from 'react';
import {
  Building2,
  BarChart3,
  Image,
  CreditCard,
  Settings,
  HelpCircle,
  Zap,
  Play,
  HardDrive,
  DollarSign,
  Cpu,
  KeyRound,
  ScrollText,
  Sliders,
  Server,
  Users,
  Gamepad2,
} from 'lucide-react';
import * as clusterService from '../services/clusterService';
import * as billingService from '../services/billingService';
import { useAuth } from '../hooks/useAuth';

const Sidebar = ({ currentPage, setCurrentPage }) => {
  const { isAdmin, isInferenceOnly } = useAuth();
  const [activeInstances, setActiveInstances] = useState(0);
  const [totalInstances, setTotalInstances] = useState(0);
  const [burningRate, setBurningRate] = useState(0);
  const [creditBalance, setCreditBalance] = useState(null);
  const [loading, setLoading] = useState(true);

  // Re-run when the role resolves: on first mount the user is still loading, so
  // isInferenceOnly is false and fetchData would request clusters it cannot see.
  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInferenceOnly]);

  const fetchData = async () => {
    try {
      const [clusterRes, balanceRes] = await Promise.all([
        // An inference-only account owns no clusters, so the instance and
        // burn-rate counters below are meaningless for it and are not rendered.
        isInferenceOnly ? Promise.resolve({ success: false }) : clusterService.listClusters(),
        billingService.getBalance(),
      ]);

      if (clusterRes.success) {
        const clusters = clusterRes.clusters || [];
        const runningClusters = clusters.filter(c => c.status === 'UP');
        setActiveInstances(runningClusters.length);
        setTotalInstances(clusters.length);
        const totalBurningRate = runningClusters.reduce((sum, cluster) => {
          return sum + parseFloat(cluster.estimated_cost_per_hour || 0);
        }, 0);
        setBurningRate(totalBurningRate);
      }

      if (balanceRes.success) {
        setCreditBalance(balanceRes.balance_cents);
      }
    } catch (err) {
      console.error('Failed to fetch sidebar data:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatCredits = (cents) => {
    if (cents === null || cents === undefined) return '--';
    return `$${(cents / 100).toFixed(2)}`;
  };

  // Inference-only clients consume endpoints; they never provision them, so
  // Dashboard, Storage, Image and the cluster list are all absent: an
  // inference account consumes models, it does not run or inspect the machines
  // behind them. INFERENCE_ALLOWED_PAGES in App.js enforces the same set, so a
  // hand-typed page cannot reach what the nav omits.
  const navItems = isInferenceOnly
    ? [
      { id: 'playground', icon: Play, label: 'Playground' },
      { id: 'payment', icon: CreditCard, label: 'Payment' },
      { id: 'settings', icon: Settings, label: 'Settings' },
      { id: 'help', icon: HelpCircle, label: 'Help' },
    ]
    : [
      { id: 'dashboard', icon: BarChart3, label: 'Dashboard' },
      { id: 'inference', icon: Zap, label: 'Inference' },
      { id: 'playground', icon: Play, label: 'Playground' },
      { id: 'storage', icon: HardDrive, label: 'Storage' },
      { id: 'image', icon: Image, label: 'Image' },
      { id: 'payment', icon: CreditCard, label: 'Payment' },
      { id: 'organizations', icon: Building2, label: 'Organizations' },
      { id: 'settings', icon: Settings, label: 'Settings' },
      { id: 'help', icon: HelpCircle, label: 'Help' },
      ...(isAdmin ? [
        { id: 'admin-billing', icon: DollarSign, label: 'Admin Billing' },
        { id: 'admin-users', icon: Users, label: 'Admin Users' },
        { id: 'admin-playground-config', icon: Gamepad2, label: 'Playground Config' },
        { id: 'admin-gpu-config', icon: Sliders, label: 'GPU Config' },
        { id: 'admin-providers', icon: KeyRound, label: 'Admin Providers' },
        { id: 'admin-accelerators', icon: Cpu, label: 'GPU Catalog' },
        { id: 'admin-logs', icon: ScrollText, label: 'Admin Logs' },
        { id: 'admin-ssh-pools', icon: Server, label: 'SSH Pools' },
      ] : []),
    ];

  return (
    <div className="w-64 bg-gray-900 text-white h-screen fixed left-0 top-0 z-10">
      <div className="p-6">
        <div className="flex items-center mb-8">
          <h1 className="text-xl font-bold">TCF-Studio</h1>
          {isAdmin && (
            <span className="ml-2 px-2 py-0.5 text-xs font-semibold bg-purple-600 text-white rounded-full">Admin</span>
          )}
          {isInferenceOnly && (
            <span className="ml-2 px-2 py-0.5 text-xs font-semibold bg-blue-600 text-white rounded-full">Inference</span>
          )}
        </div>

        <div className="bg-gray-800 rounded-lg p-4 mb-6">
          <div className="flex justify-between items-center mb-2 cursor-pointer hover:opacity-80" onClick={() => setCurrentPage('payment')}>
            <span className="text-sm text-gray-400">Credits</span>
            <span className={`font-semibold ${creditBalance > 0 ? 'text-green-400' : creditBalance === 0 ? 'text-red-400' : 'text-gray-500'}`}>
              {loading ? '...' : formatCredits(creditBalance)}
            </span>
          </div>
          {!isInferenceOnly && (
            <>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-gray-400">Active Instances</span>
                <span className="text-blue-400 font-semibold">
                  {loading ? '...' : `${activeInstances}/${totalInstances}`}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-400">Burning Rate</span>
                <span className={`font-semibold ${burningRate > 0 ? 'text-orange-400' : 'text-gray-500'}`}>
                  {loading ? '...' : burningRate > 0 ? `$${burningRate.toFixed(3)}/hr` : '$0.000/hr'}
                </span>
              </div>
            </>
          )}
        </div>

      <nav className="space-y-2">
        {navItems.map(item => (
          <button
            key={item.id}
            onClick={() => setCurrentPage(item.id)}
            className={`w-full flex items-center space-x-3 p-3 rounded-lg transition-colors ${
              currentPage === item.id ? 'bg-purple-600' : 'hover:bg-gray-800'
            }`}
          >
            <item.icon size={20} />
            <span>{item.label}</span>
          </button>
        ))}
      </nav>
    </div>
  </div>
  );
};

export default Sidebar;